import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { dashboardAPI } from '../api/dashboard'
import { branchChanged } from './branchSlice'
import type { DashboardHomeParams } from '../api/dashboard'
import type { DashboardHomeData } from '../pages/Dashboard/types'

interface DashboardState {
 data: any
 globalAnalytics: any
 branchOperational: any
 home: DashboardHomeData | null
 homeLoading: boolean
 homeRequestId: string | null
 loading: boolean
 globalLoading: boolean
 branchLoading: boolean
 error: string | null
}

const initialState: DashboardState = {
 data: null,
 globalAnalytics: null,
 branchOperational: null,
 home: null,
 homeLoading: false,
 homeRequestId: null,
 loading: false,
 globalLoading: false,
 branchLoading: false,
 error: null,
}

export const fetchStatistics = createAsyncThunk(
 'dashboard/fetchStatistics',
 async (_, { rejectWithValue }) => {
 try {
 const response = await dashboardAPI.getStatistics()
 return response.data ?? response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.dashboard.statsFailed'))
 }
 }
)

export const fetchGlobalAnalytics = createAsyncThunk(
 'dashboard/fetchGlobalAnalytics',
 async (_, { rejectWithValue }) => {
 try {
 const response = await dashboardAPI.getGlobalAnalytics()
 return response.data ?? response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.dashboard.analyticsFailed'))
 }
 }
)

/** استجابة Laravel: { status, data: حمولة لوحة الفرع (branch, kpis, …), message } — نخزّن الحقل الداخلي data ليطابق BranchDashboard (kpis.today_expenses). */
export const fetchBranchOperational = createAsyncThunk(
 'dashboard/fetchBranchOperational',
 async (_, { rejectWithValue }) => {
 try {
 const response = await dashboardAPI.getBranchOperational()
 return response.data ?? response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.dashboard.branchOpsFailed'))
 }
 }
)

export const fetchHome = createAsyncThunk<DashboardHomeData, DashboardHomeParams, { rejectValue: string }>(
 'dashboard/fetchHome',
 async (params, { rejectWithValue }) => {
  try {
   const response = await dashboardAPI.getHome(params)
   return (response.data ?? response) as DashboardHomeData
  } catch (error: any) {
   return rejectWithValue(error.response?.data?.message || i18n.t('errors.dashboard.fetchFailed'))
  }
 },
)

const dashboardSlice = createSlice({
 name: 'dashboard',
 initialState,
 reducers: {
 clearDashboard: (state) => {
 state.data = null
 state.globalAnalytics = null
 state.branchOperational = null
 state.error = null
 },
 },
 extraReducers: (builder) => {
 builder
 .addCase(fetchStatistics.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(fetchStatistics.fulfilled, (state, action) => {
 state.loading = false
 state.data = action.payload
 })
 .addCase(fetchStatistics.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 .addCase(fetchGlobalAnalytics.pending, (state) => {
 state.globalLoading = true
 })
 .addCase(fetchGlobalAnalytics.fulfilled, (state, action) => {
 state.globalLoading = false
 state.globalAnalytics = action.payload
 })
 .addCase(fetchGlobalAnalytics.rejected, (state) => {
 state.globalLoading = false
 })
 .addCase(fetchBranchOperational.pending, (state) => {
 state.branchLoading = true
 })
 .addCase(fetchBranchOperational.fulfilled, (state, action) => {
 state.branchLoading = false
 state.branchOperational = action.payload
 })
 .addCase(fetchBranchOperational.rejected, (state) => {
 state.branchLoading = false
 })
 .addCase(fetchHome.pending, (state, action) => {
  state.error = null
  state.homeLoading = true
  state.homeRequestId = action.meta.requestId
 })
 .addCase(fetchHome.fulfilled, (state, action) => {
  if (state.homeRequestId !== action.meta.requestId) return
  state.homeLoading = false
  state.home = action.payload
  state.error = null
  state.homeRequestId = null
 })
 .addCase(fetchHome.rejected, (state, action) => {
  if (state.homeRequestId !== action.meta.requestId) return
  state.homeLoading = false
  state.homeRequestId = null
  state.error = action.payload ?? i18n.t('errors.dashboard.fetchFailed')
 })
 .addCase(branchChanged, (state) => {
 state.data = null
 state.globalAnalytics = null
 state.branchOperational = null
 state.loading = false
 state.globalLoading = false
 state.branchLoading = false
 state.home = null
 state.homeLoading = false
 state.homeRequestId = null
 state.error = null
 })
 },
})

export const { clearDashboard } = dashboardSlice.actions
export default dashboardSlice.reducer
